import { isString } from 'fitify-types/src/type-guards/common'
import { PlanInterval } from 'fitify-types/src/types/subscription'
import { getItem, setItem } from 'fitify-ui/src/utils/storage'
import { useRouter } from 'next/router'
import {
  createContext,
  type Dispatch,
  type FC,
  type PropsWithChildren,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  type UseGrowthBook,
  useGrowthBook,
} from '../utils/growthbook/generated'
import { setPromoCodeNavigationCache } from '../utils/navigation'

const ACTIVE_PROMO_STORE_KEY = '@fitify/subscription/activePromo'

const getPromoCode = (
  queryPromo: unknown,
  defaultPromo: string | undefined
): string | undefined => {
  if (isString(queryPromo)) {
    return queryPromo
  }

  const storePromo = getItem('session', ACTIVE_PROMO_STORE_KEY)
  if (isString(storePromo)) {
    return storePromo
  }

  return defaultPromo || undefined
}

const getDefaultPromoCode = (growthBook: UseGrowthBook) => {
  const defaultPromoFeatureValue =
    growthBook.evalFeature('onboarding_promo')?.value
  return isString(defaultPromoFeatureValue)
    ? defaultPromoFeatureValue
    : undefined
}

const isDefaultPromoCode = (
  promoCode: string | undefined,
  defaultPromo: string | undefined
) => {
  return isString(promoCode) && promoCode === defaultPromo
}

const isPromoCodeEnabled = (
  currentPromoCode: string,
  growthBook: UseGrowthBook
): boolean => {
  return Object.values(PlanInterval).some((interval) => {
    return (
      growthBook.evalFeature(`promo_${currentPromoCode}_${interval}_enabled`)
        ?.value === true
    )
  })
}

type ContextProps = {
  handleSetPromoCode: (newPromoCode: string) => void
  isDefaultPromo: boolean
  isInitialized: boolean
  isIsicVerified: boolean
  promoCode?: string
  setIsIsicVerified?: Dispatch<SetStateAction<boolean>>
}

const Context = createContext<ContextProps>({
  // eslint-disable-next-line no-empty-function
  handleSetPromoCode: () => {},
  isDefaultPromo: false,
  isInitialized: false,
  isIsicVerified: false,
})

export const PromoContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const router = useRouter()
  const [isDefaultPromo, setIsDefaultPromo] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const [isIsicVerified, setIsIsicVerified] = useState(false)
  const [promoCode, setPromoCode] = useState<string | undefined>(undefined)
  const growthBook = useGrowthBook()

  const handleSetPromoCode = useCallback(
    (newPromoCode: string) => {
      if (isPromoCodeEnabled(newPromoCode, growthBook)) {
        const defaultPromoFeature = getDefaultPromoCode(growthBook)
        setPromoCode(newPromoCode)
        setPromoCodeNavigationCache(newPromoCode)
        setIsDefaultPromo(isDefaultPromoCode(newPromoCode, defaultPromoFeature))
        setItem('session', ACTIVE_PROMO_STORE_KEY, newPromoCode)
      }
    },
    [growthBook]
  )

  useEffect(() => {
    if (!router.isReady || !growthBook.ready) {
      return
    }

    const defaultPromoFeature = getDefaultPromoCode(growthBook)
    const currentPromoCode = getPromoCode(
      router.query.promo,
      defaultPromoFeature
    )
    if (currentPromoCode && isPromoCodeEnabled(currentPromoCode, growthBook)) {
      handleSetPromoCode(currentPromoCode)
    }

    setIsInitialized(true)
  }, [growthBook, handleSetPromoCode, router.isReady, router.query.promo])

  const value = useMemo(
    () => ({
      handleSetPromoCode,
      isDefaultPromo,
      isInitialized,
      isIsicVerified,
      promoCode,
      setIsIsicVerified,
    }),
    [
      handleSetPromoCode,
      isDefaultPromo,
      isInitialized,
      isIsicVerified,
      promoCode,
      setIsIsicVerified,
    ]
  )

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export const usePromoContext = () => {
  const context = useContext(Context)
  if (context === undefined) {
    throw new Error(
      'usePromoContext must be used within a PromoContextProvider'
    )
  }
  return context
}
